/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import moment from 'moment';

import { Section } from '../config/section';
import { CoreServices } from './CoreServices';
import { DataManagement } from './DataManagement';
import {
  AiUserCountDTO,
  DashboardView,
  FunctionalAreaDto,
  ICCreateDealspace,
  ICDealspace,
  ICFreeText,
  ICPlaybook,
  ICSelectPlaybook,
  ICSubTaskCreate,
  ICTask,
  ISCreateComment,
  ISCreateDecision,
  ISCreateDependency,
  ISCreateIssue,
  ISCreateRisk,
  ISCreateSection,
  ISError,
  ISServiceResponse,
  ISSuccess,
  ISUpdateCoreDecision,
  ISUpdateCoreIssue,
  ISUpdateCoreProject,
  ISUpdateCoreProjectUser,
  ISUpdateCoreRisk,
  ISUpdateCoreTask,
  ISUpdateMergefloDealspaceUser,
  ISUpdateMergefloDependency,
  ISUpdateMergefloTask,
  ISUpdateTaskProject,
  PaymentDto,
  PlayBookRequest,
  SubscriptionType,
} from './Interfaces';
import { MergefloServices } from './MergefloServices';

export function useEffectAsync(effect: () => void, inputs: any) {
  useEffect(() => {
    effect();
  }, inputs);
}

class ComponentHelper {
  coreServices: CoreServices;
  mergefloServices: MergefloServices;
  dataManagement: DataManagement;
  constructor() {
    this.coreServices = new CoreServices();
    this.mergefloServices = new MergefloServices();
    this.dataManagement = new DataManagement();
  }

  unsecuredCopyToClipboard(text: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      console.log('insecure connection');
    }
  }

  validateEmail(email: string) {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  }
  isDealspaceTrialEnd(pricingReducer: any) {
    return (
      pricingReducer.pricingType === SubscriptionType.TRIAL &&
      pricingReducer.trialEndDate !== '' &&
      moment(new Date()).diff(moment(pricingReducer.trialEndDate)) > 0
    );
  }

  async loadData(playbookID: string) {
    const incomingSections = (await this.coreServices.listProjectSections(
      playbookID
    )) as Section[];
    let currentSections = incomingSections.sort((a, b) =>
      a.sectionOrder! > b.sectionOrder! ? 1 : -1
    );
    const newSectionList = [] as Section[];
    for (let index = 0; index < currentSections.length; index++) {
      newSectionList.push({
        id: currentSections[index].id,
        name: currentSections[index].name,
        projectId: currentSections[index].projectId,
        createdAt: currentSections[index].createdAt,
        createdBy: currentSections[index].createdBy,
        isArchived: currentSections[index].isArchived,
        sectionOrder: index + 1,
        updatedAt: currentSections[index].updatedAt,
        updatedBy: currentSections[index].updatedBy,
      });
    }

    const userList = await this.coreServices.listProjectUsers(playbookID);
    return {
      section: newSectionList,
      userList: userList,
    };
  }

  async forgotPassword(username: string) {
    return this.coreServices.forgotPasswordEmail(username);
  }

  async listSection(playbookId: string) {
    return this.coreServices.listProjectSections(playbookId);
  }

  async updateProject(id: string, newValues: ISUpdateCoreProject) {
    return this.coreServices.updateProject(id, newValues);
  }

  async deleteProjectUser(id: string) {
    return this.coreServices.deleteProjectUser(id);
  }

  async updateProjectUser(id: string, newValues: ISUpdateCoreProjectUser) {
    return this.coreServices.updateProjectUser(id, newValues);
  }

  async deleteDealspace(id: string) {
    return this.mergefloServices.deleteDealspace(id);
  }

  async reminderMailForDealspaceuser(dealspaceUserId: string) {
    return this.mergefloServices.reminderEmailForProjectUser(dealspaceUserId);
  }

  async reminderMailForProject(projectUserId: string) {
    return this.coreServices.reminderEmailForProjectUser(projectUserId);
  }

  async inviteProject(
    username: string[],
    playbookId: string,
    invitationMessage: string
  ) {
    const response = await this.coreServices.inviteProject(
      username,
      playbookId,
      invitationMessage
    );
    return response;
  }

  async createDealspaceUser(
    dealspaceId: string,
    username: string[],
    functionalAreaId: string,
    invitationMessage: string
  ) {
    const response = await this.mergefloServices.createDealspaceUser(
      dealspaceId,
      username,
      functionalAreaId,
      invitationMessage
    );
    return response; //createFunctionalArea
  }

  async listFunctionalAreas() {
    const functionalAreas = await this.mergefloServices.listFunctionalAreas();
    return functionalAreas;
  }

  async listFunctionalAreasByDealspaceId(dealspaceId: string) {
    const functionalAreas =
      await this.mergefloServices.listFunctionalAreasByDealspaceId(dealspaceId);
    return functionalAreas;
  }

  async createFunctionalArea(functionalAreaDto: FunctionalAreaDto) {
    const functionalAreas = await this.mergefloServices.createFunctionalArea(
      functionalAreaDto
    );
    return functionalAreas;
  }

  async updateFunctionalArea(id: string, functionalAreaDto: FunctionalAreaDto) {
    const functionalAreas = await this.mergefloServices.updateFunctionalArea(
      id,
      functionalAreaDto
    );
    return functionalAreas;
  }

  async deleteFunctionalArea(id: string) {
    const templateResponse = await this.mergefloServices.deleteFunctionalArea(
      id
    );
    return templateResponse;
  }

  async buildFromTemplate(playbookId: string) {
    const templateResponse = await this.mergefloServices.buildFromTemplate(
      playbookId
    );
    return templateResponse;
  }

  async deleteDealspaceUser(id: string) {
    const templateResponse = await this.mergefloServices.deleteDealspaceUser(
      id
    );
    return templateResponse;
  }

  async changeDealspaceUser(
    id: string,
    newValues: ISUpdateMergefloDealspaceUser
  ) {
    return this.mergefloServices.changeDealspaceUser(id, newValues);
  }

  async listTaskComments(taskId: string) {
    const taskComments = await this.coreServices.listTaskComments(taskId);
    return taskComments;
  }

  async listTaskLogs(taskId: string) {
    return this.coreServices.listTaskLogs(taskId);
  }

  async listDealspaceUsers(id: string) {
    return this.mergefloServices.listDealspaceUsers(id);
  }

  async changeFamilyOrder(
    taskId: string,
    referenceTaskId: string,
    isBefore: boolean
  ) {
    return this.coreServices.changeFamilyOrder(
      taskId,
      referenceTaskId,
      isBefore
    );
  }

  async changeParentTaskAndOrder(
    parentId: string | null,
    taskId: string,
    referenceTaskId: string | null,
    isBefore: boolean
  ) {
    return this.coreServices.changeParentTaskAndOrder(
      parentId,
      taskId,
      referenceTaskId,
      isBefore
    );
  }

  async changeSectionOrder(
    taskId: string,
    referenceTaskId: string,
    projectId: string,
    isBefore: boolean
  ) {
    return this.coreServices.changeSectionOrder(
      taskId,
      referenceTaskId,
      projectId,
      isBefore
    );
  }

  async updateSectionOrder(
    sectionId: string,
    referenceSectionId: string,
    isBefore: boolean
  ) {
    return this.coreServices.updateSectionOrder(
      sectionId,
      referenceSectionId,
      isBefore
    );
  }

  async convertToMilestone(taskId: string) {
    const convertToMilestone = await this.coreServices.convertToMilestone(
      taskId
    );
    return convertToMilestone;
  }

  async milestoneToTask(taskId: string) {
    const milestoneToTask = await this.coreServices.milestoneToTask(taskId);
    return milestoneToTask;
  }

  async getDealspaceTasks(workspaceId: string) {
    const myTasks = await this.mergefloServices.getDealspaceTasks(workspaceId);
    return myTasks;
  }

  async getDealspacePrefixes(workspaceId: string) {
    const myTasks = await this.mergefloServices.getDealspacePrefixes(
      workspaceId
    );
    return myTasks;
  }

  async createPlaybook(
    playbookName: string,
    functionalAreaId: string,
    workspaceId: string,
    taskPrefix: string
  ) {
    let createPlaybook = await this.mergefloServices.createPlaybook(
      playbookName,
      functionalAreaId,
      taskPrefix,
      workspaceId
    );
    return createPlaybook;
  }
  async createDealSpace(dealspaceInfo: ICCreateDealspace) {
    let dealspaceCreate = await this.mergefloServices.createDealspace(
      dealspaceInfo.dealspaceName
    );
    if (dealspaceCreate.id !== undefined) {
      let createPlaybook = await this.mergefloServices.createPlaybook(
        dealspaceInfo.playbookName,
        dealspaceInfo.functionalAreaId,
        dealspaceInfo.taskPrefix,
        dealspaceCreate.id
      );

      if (createPlaybook.data.id !== undefined) {
        let success: ISSuccess = {
          message: 'Success.',
          data: createPlaybook.data,
        };
        let response: ISServiceResponse = {
          statusCode: '201',
          success: success,
        };
        return response;
      } else {
        let error: ISError = { messaege: createPlaybook.messaege };
        let response: ISServiceResponse = {
          statusCode: createPlaybook?.data?.statusCode,
          error: error,
        };
        return response;
      }
    } else {
      let error: ISError = { messaege: dealspaceCreate.messaege };
      let response: ISServiceResponse = {
        statusCode: dealspaceCreate.statusCode,
        error: error,
      };
      return response;
    }
  } //

  async createDealSpaceEmpty(dealspaceInfo: ICCreateDealspace) {
    let dealspaceCreate = await this.mergefloServices.createDealspace(
      dealspaceInfo.dealspaceName
    );
    if (dealspaceCreate.id !== undefined) {
      let success: ISSuccess = {
        message: 'Success.',
        data: dealspaceCreate.id,
      };
      let response: ISServiceResponse = {
        statusCode: '201',
        success: success,
      };
      return response;
    } else {
      let error: ISError = { messaege: '' };
      let response: ISServiceResponse = {
        statusCode: '',
        error: error,
      };
      return response;
    }
  } //

  async addToSubscription(playbookId: string) {
    return this.mergefloServices.addToSubscription(playbookId);
  }

  async removeFromSubscription(playbookId: string) {
    return this.mergefloServices.removeFromSubscription(playbookId);
  }

  async retrieveDealspace(dealspaceId: string) {
    return this.mergefloServices
      .retrieveDealspace(dealspaceId)
      .then(response => {
        let dealspace: ICDealspace = {
          name: response.workspace.name,
          id: response.id,
          signDate: response.signDate,
          closeDate: response.closeDate,
          dayOneDate: response.dayOneDate,
          buyerCompany: response.buyerCompany,
          sellerCompany: response.sellerCompany,
        };
        return dealspace;
      })
      .catch(response => {
        let dealspace: ICDealspace = {
          id: '',
          name: '',
          signDate: '',
          closeDate: '',
          dayOneDate: '',
          buyerCompany: '',
          sellerCompany: '',
        };
        return dealspace;
      });
  }

  async updateDealspace(dealspaceId: string, dealspace: ICDealspace) {
    return this.mergefloServices.updateDealspace(dealspaceId, dealspace);
  }

  async getDealspaceDirectory(dealspaceId: string) {
    const dealspaceDirectory = await this.mergefloServices.getDirectory(
      dealspaceId
    );
    return dealspaceDirectory;
  }

  async retrieveDealspaceUser(dealspaceId: string) {
    const dealspaceDirectory =
      await this.mergefloServices.retrieveDealspaceUser(dealspaceId);
    return dealspaceDirectory;
  }

  async getWorkspaceUserList(workspaceId: string) {
    const workspaceUser = await this.mergefloServices.getWorkspaceUserList(
      workspaceId
    );
    return workspaceUser;
  }

  async createDealspaceSubscribe(paymentDto: PaymentDto) {
    return this.mergefloServices.createDealspaceSubscribe(paymentDto);
  }

  async aiUserCount(dealspaceId: string) {
    return this.mergefloServices.aiUserCount(dealspaceId);
  }

  async aiFeaturePrice(dealspaceId: string) {
    return this.mergefloServices.aiFeaturePrice(dealspaceId);
  }

  async downgradeDealspace(dealspaceId: string) {
    return this.mergefloServices.downgradeDealspace(dealspaceId);
  }

  async createSection(sectionProperties: ISCreateSection) {
    return this.coreServices.createProjectSection(
      sectionProperties.projectId,
      sectionProperties.sectionName
    );
  }

  async createSubTask(task: ICSubTaskCreate) {
    return this.mergefloServices.createSubTask(task.name, task.parentId);
  }

  async createIssue(task: ISCreateIssue) {
    return this.mergefloServices.createIssue(task.name, task.parentId);
  }

  async createComment(comment: ISCreateComment) {
    let commentCreate = await this.coreServices.createTaskComment(
      comment.taskId,
      comment.comment
    );
    if (commentCreate.id !== undefined) {
      let success: ISSuccess = { message: 'Success.', data: this.createTask };
      let response: ISServiceResponse = {
        statusCode: '201',
        success: success,
        data: commentCreate,
      };
      return response;
    } else {
      let error: ISError = { messaege: commentCreate.messaege };
      let response: ISServiceResponse = {
        statusCode: commentCreate.statusCode,
        error: error,
      };
      return response;
    }
  }

  async resetPassword(oldPassword: string, resetPassword: string) {
    return this.coreServices.resetPassword(oldPassword, resetPassword);
  }
  async createRisk(task: ISCreateRisk) {
    return this.mergefloServices.createRisk(task.name, task.parentId);
  }

  async createDecision(task: ISCreateDecision) {
    return this.mergefloServices.createDecision(task.name, task.parentId);
  }

  async createDependency(task: ISCreateDependency) {
    let taskCreate = await this.mergefloServices.createDependency(
      task.name,
      task.parentId,
      task.functionalAreaId
    );
    return taskCreate;
  }

  async createTaskAttachment(taskId: string, attachment: any) {
    return this.coreServices
      .createTaskAttachment(taskId, attachment)
      .then(response => response);
  }

  async getTaskAttachments(taskId: string) {
    return this.coreServices
      .listTaskAttachments(taskId)
      .then(response => response);
  }

  async getNotificationSetup() {
    return this.coreServices.getNotificationSetup().then(response => response);
  }

  async getUserNotifications(
    workspaceId: string,
    read: boolean,
    page: number,
    perPage: number,
    startDate: Date | null,
    endDate: Date | null,
    assignedToMe: boolean,
    taskUpdate: boolean
  ) {
    return this.coreServices
      .listUserNotifications(
        workspaceId,
        read,
        page,
        perPage,
        startDate,
        endDate,
        assignedToMe,
        taskUpdate
      )
      .then(response => response);
  }

  async markAsReadNotification(notificationId: string) {
    return this.coreServices
      .markAsReadNotification(notificationId)
      .then(response => response);
  }

  async markAsUnReadNotification(notificationId: string) {
    return this.coreServices
      .markAsUnReadNotification(notificationId)
      .then(response => response);
  }

  async markAllAsReadNotification(notificationIds: string[]) {
    return this.coreServices
      .markAllAsReadNotification(notificationIds)
      .then(response => response);
  }

  async markAllAsUnreadNotification(notificationIds: string[]) {
    return this.coreServices
      .markAllAsUnreadNotification(notificationIds)
      .then(response => response);
  }

  async unReadNotificationCount(dealspaceId: string) {
    return this.coreServices
      .unReadNotificationCount(dealspaceId)
      .then(response => response);
  }

  async createTask(task: ICTask) {
    return this.coreServices.createTask(
      task.name!,
      task.dealspaceId ? task.dealspaceId : '',
      task.playbookId ? task.playbookId : '',
      task.sectionId ? task.sectionId : ''
    );
  }

  async createTaskWithReference(task: ICTask, referenceTaskId: string) {
    return this.coreServices.createTask(
      task.name!,
      task.dealspaceId ? task.dealspaceId : '',
      task.playbookId ? task.playbookId : '',
      task.sectionId ? task.sectionId : '',
      referenceTaskId
    );
  }

  async createRiskWithReference(
    task: ISCreateRisk,
    referenceTaskId: string,
    isBefore: boolean
  ) {
    return this.mergefloServices.createRisk(
      task.name,
      task.parentId,
      referenceTaskId,
      isBefore
    );
  }

  async createDecisionWithReference(
    task: ISCreateDecision,
    referenceTaskId: string,
    isBefore: boolean
  ) {
    return this.mergefloServices.createDecision(
      task.name,
      task.parentId,
      referenceTaskId,
      isBefore
    );
  }

  async createSubTaskWithReference(
    task: ICSubTaskCreate,
    referenceTaskId: string,
    isBefore: boolean
  ) {
    return this.mergefloServices.createSubTask(
      task.name,
      task.parentId,
      referenceTaskId,
      isBefore
    );
  }

  async createIssueWithReference(
    task: ISCreateIssue,
    referenceTaskId: string,
    isBefore: boolean
  ) {
    return this.mergefloServices.createIssue(
      task.name,
      task.parentId,
      referenceTaskId,
      isBefore
    );
  }

  async updateSection(sectionId: string, name: string) {
    const updateResponse = await this.coreServices.updateProjectSection(
      sectionId,
      { name: name }
    );
    return updateResponse;
  }

  async archiveSection(sectionId: string, value: boolean) {
    const updateResponse = await this.coreServices.updateProjectSection(
      sectionId,
      { isArchived: value } as any
    );
    return updateResponse;
  }

  async updateRisk(riskId: string, newValues: ISUpdateCoreRisk) {
    const updateResponse = await this.coreServices.updateRisk(
      riskId,
      newValues
    );
    return updateResponse;
  }

  async updateDecision(decisionId: string, newValues: ISUpdateCoreDecision) {
    const updateResponse = await this.coreServices.updateDecision(
      decisionId,
      newValues
    );
    return updateResponse;
  }

  async updateDependency(
    dependencyId: string,
    newValues: ISUpdateMergefloDependency
  ) {
    const updateResponse = await this.mergefloServices.updateDependency(
      dependencyId,
      newValues
    );
    return updateResponse;
  }

  async updateIssue(issueId: string, newValues: ISUpdateCoreIssue) {
    const updateResponse = await this.coreServices.updateIssue(
      issueId,
      newValues
    );
    return updateResponse;
  }

  async getTaskDetail(taskId: string) {
    const taskDetail = await this.mergefloServices.retrieveTask(taskId);
    return taskDetail;
  }

  async updateTaskSection(dto: ISUpdateTaskProject) {
    const taskDetail = await this.coreServices.updateTaskSection(dto);
    return taskDetail;
  }

  async deleteTask(taskId: string) {
    const deleteResponse = await this.coreServices.deleteTask(taskId);
    return deleteResponse;
  }

  async deleteUndoTask(taskId: string, requestBody: any) {
    const deleteResponse = await this.coreServices.deleteUndoTask(
      taskId,
      requestBody
    );
    return deleteResponse;
  }

  async deleteSection(sectionId: string, moveTasks: boolean) {
    const sectionDeletion = await this.coreServices.deleteProjectSection(
      sectionId,
      moveTasks
    );
    return sectionDeletion;
  }

  async undoDeleteSection(sectionId: string, undoParams: any) {
    const sectionDeletion = await this.coreServices.undoDeleteSection(
      sectionId,
      undoParams
    );
    return sectionDeletion;
  }

  async updateTask(newValues: ICTask) {
    let coreServiceParams: ISUpdateCoreTask = {};
    let mergefloServiceParams: ISUpdateMergefloTask = {};
    let taskProjectParams: ISUpdateTaskProject = {
      taskId: newValues.id ? newValues.id : '',
      projectId: newValues.projectId ? newValues.projectId : '',
      sectionId: '',
    };
    let updateTaskKeys = Object.keys(newValues);
    Array.from(updateTaskKeys).forEach(key => {
      switch (key) {
        case 'name': {
          coreServiceParams.name = newValues.name;
          break;
        }
        case 'sectionId': {
          taskProjectParams.sectionId = newValues.sectionId
            ? newValues.sectionId
            : '';
          break;
        }
        case 'acknowledged': {
          mergefloServiceParams.acknowledged = newValues.acknowledged;
          break;
        }
        case 'complianceType': {
          mergefloServiceParams.complianceType = newValues.complianceType;
          break;
        }
        case 'dayOne': {
          mergefloServiceParams.dayOne = newValues.dayOne;

          break;
        }
        case 'focusArea': {
          mergefloServiceParams.focusArea = newValues.focusArea;

          break;
        }
        case 'integrationDueDiligence': {
          mergefloServiceParams.integrationDueDiligence =
            newValues.integrationDueDiligence;

          break;
        }
        case 'integrationPlanning': {
          mergefloServiceParams.integrationPlanning =
            newValues.integrationPlanning;

          break;
        }
        case 'oneTimeCost': {
          mergefloServiceParams.oneTimeCost = newValues.oneTimeCost;

          break;
        }
        case 'synergyAchieved': {
          mergefloServiceParams.synergyAchieved = newValues.synergyAchieved;

          break;
        }
        case 'targetSynergy': {
          mergefloServiceParams.targetSynergy = newValues.targetSynergy;

          break;
        }
        case 'assignee': {
          coreServiceParams.assignee = newValues.assignee ?? null;

          break;
        }
        case 'description': {
          coreServiceParams.description = newValues.description;
          break;
        }
        case 'dueDate': {
          coreServiceParams.dueDate = newValues.dueDate;
          break;
        }
        case 'isCompleted': {
          coreServiceParams.isCompleted = newValues.isCompleted;
          break;
        }
        case 'isArchived': {
          coreServiceParams.isArchived = newValues.isArchived;
          break;
        }
        case 'parentId': {
          coreServiceParams.parentId = newValues.parentId;
          break;
        }
        case 'priority': {
          coreServiceParams.priority = newValues.priority;
          break;
        }
        case 'startDate': {
          coreServiceParams.startDate = newValues.startDate;
          break;
        }
      }
    });

    if (Object.keys(mergefloServiceParams).length > 0)
      return this.mergefloServices.updateTask(
        newValues.id ? newValues.id : '',
        mergefloServiceParams
      );
    if (Object.keys(coreServiceParams).length > 0)
      return this.coreServices.updateTask(
        newValues.id ? newValues.id : '',
        coreServiceParams
      );
    if (taskProjectParams.sectionId !== '')
      return this.coreServices.updateTaskSection(taskProjectParams);
  }

  getColorFromDateDistance(distance: number): string {
    if (distance > 0) {
      if (distance === 0) return 'black';
      if (distance < 2) return 'orange';
      return 'green';
    } else return 'red';
  }

  async getDealspaceFreeText(
    workspaceId: string,
    type: 'playbook' | 'default'
  ) {
    const myFreetext = await this.mergefloServices.getDealspaceFreeText(
      workspaceId,
      type
    );
    return myFreetext;
  }

  async createDealspaceFreeText(
    workspaceId: string,
    title: string,
    data: string,
    type: 'playbook' | 'default'
  ) {
    const myFreetext = await this.mergefloServices.createDealspaceFreeText(
      workspaceId,
      title,
      data,
      type
    );
    return myFreetext;
  }

  async updateDealspaceFreeText(workspaceId: string, freetext: ICFreeText) {
    const myFreetext = await this.mergefloServices.updateDealspaceFreeText(
      workspaceId,
      freetext
    );
    return myFreetext;
  }

  async deleteDealspaceFreeText(id: string) {
    const myFreetext = await this.mergefloServices.deleteDealspaceFreeText(id);
    return myFreetext;
  }

  async getDealBackground(workspaceId: string) {
    const text = await this.mergefloServices.getDealBackground(workspaceId);
    return text;
  }

  async createDealBackground(workspaceId: string, data: string) {
    const text = await this.mergefloServices.createDealBackground(
      workspaceId,
      data
    );
    return text;
  }

  async updateDealBackground(workspaceId: string, freetext: ICFreeText) {
    const text = await this.mergefloServices.updateDealBackground(
      workspaceId,
      freetext
    );
    return text;
  }

  async deleteDealBackground(id: string) {
    const text = await this.mergefloServices.deleteDealBackground(id);
    return text;
  }

  async getAccomplishments(workspaceId: string, type: 'playbook' | 'default') {
    const text = await this.mergefloServices.getAccomplishments(
      workspaceId,
      type
    );
    return text;
  }

  async bootTemplates(id: string, templateCollection: string[]) {
    return this.mergefloServices.bootTemplates(id, templateCollection);
  }

  async importFromTemplates(id: string, templateCollection: string[]) {
    return this.mergefloServices.importFromTemplates(id, templateCollection);
  }

  async importFromPersonalTemplates(id: string, templateCollection: string[]) {
    return this.mergefloServices.importFromPersonalTemplates(
      id,
      templateCollection
    );
  }

  async listPlaybooks(id: string) {
    return this.mergefloServices.listPlaybooks(id);
  }

  async listTemplateCollection() {
    const text = await this.mergefloServices.listTemplateCollection();
    return text;
  }

  async portalLink(link: string) {
    const text = await this.mergefloServices.portalLink(link);
    return text;
  } //

  async dealspaceSubscribeCancel(dealspaceId: string) {
    const text = await this.mergefloServices.dealspaceSubscribeCancel(
      dealspaceId
    );
    return text;
  }

  async workstreamPrice(dealspaceId: string) {
    const text = await this.mergefloServices.workstreamPrice(dealspaceId);
    return text;
  }

  async getDealspaceSubscribe(dealspaceId: string) {
    const text = await this.mergefloServices.getDealspaceSubscribe(dealspaceId);
    return text;
  }

  async getDealspaceSubscribe2(dealspaceId: string) {
    const text = await this.mergefloServices.getDealspaceSubscribe2(
      dealspaceId
    );
    return text;
  }

  async createAccomplishments(
    workspaceId: string,
    data: string,
    date: string,
    type: 'playbook' | 'default'
  ) {
    const text = await this.mergefloServices.createAccomplishments(
      workspaceId,
      data,
      date,
      type
    );
    return text;
  }

  async updateAccomplishments(workspaceId: string, freetext: ICFreeText) {
    const text = await this.mergefloServices.updateAccomplishments(
      workspaceId,
      freetext
    );
    return text;
  }

  async deleteAccomplishments(id: string) {
    const text = await this.mergefloServices.deleteAccomplishments(id);
    return text;
  }

  async getDashboardWidget(
    workspaceId: string,
    type: 'Imo' | 'Workstream' | 'Executive'
  ) {
    const text = await this.mergefloServices.getDashboardWidget(
      workspaceId,
      type
    );
    return text;
  }
}

class UserHelper {
  coreServices: CoreServices;
  mergefloServices: MergefloServices;
  dataManagement: DataManagement;

  constructor() {
    this.coreServices = new CoreServices();
    this.mergefloServices = new MergefloServices();
    this.dataManagement = new DataManagement();
  }

  async registerUser(
    email: string,
    name: string,
    surName: string,
    password: string,
    playbookName?: string | null
  ) {
    const registerResponse = await this.coreServices.registerUser(
      email,
      name,
      surName,
      password,
      playbookName
    );
    if (registerResponse.username !== undefined) {
      let success: ISSuccess = { message: 'Success.', data: registerResponse };
      let response: ISServiceResponse = {
        statusCode: '201',
        success: success,
        data: registerResponse,
      };
      return response;
    } else {
      let error: ISError = {
        messaege: registerResponse.messaege || registerResponse,
      };
      let response: ISServiceResponse = {
        statusCode: registerResponse.statusCode,
        error: error,
      };
      return response;
    }
  }

  async getExcel(id: string, type: string) {
    return await this.coreServices.getExcel(id, type);
  }

  async loginUser(email: string, password: string) {
    const loginResponse = await this.coreServices.loginUser(email, password);
    console.log('loginResponse', loginResponse);

    if (loginResponse?.data?.idToken !== undefined) {
      let success: ISSuccess = {
        message: 'Success.',
        data: loginResponse.data,
      };
      let response: ISServiceResponse = {
        statusCode: '201',
        success: success,
        data: loginResponse.data,
      };
      return response;
    } else {
      let error: ISError = {
        messaege: loginResponse.message ? loginResponse.message : '',
      };
      let response: ISServiceResponse = {
        statusCode: loginResponse.statusCode ? loginResponse.statusCode : '',
        error: error,
      };
      return response;
    }
  }

  async getAssets() {
    return this.coreServices.getUserAssets();
  }

  async getUserDealspaces() {
    return this.coreServices.getUserDealspaces();
  }

  async getSignedUserAssets() {
    return this.coreServices
      .getUserAssets()
      .then(response => {
        return response;
      })
      .catch(response => {
        return null;
      });
  }

  async getPlaybookList(dealspaceId: string) {
    return this.coreServices
      .getUserAssets()
      .then(response => {
        let playbookList: ICPlaybook[] = [];
        for (let i = 0; i < response.playbooks.length; i++) {
          if (dealspaceId === response.playbooks[i].dealspaceId) {
            let currentPlaybook: ICPlaybook = {
              id: response.playbooks[i].id,
              name: response.playbooks[i].name,
            };
            playbookList.push(currentPlaybook);
          }
        }
        return playbookList;
      })
      .catch(response => {
        return [];
      });
  }

  async getAllPlaybook() {
    return this.coreServices
      .getUserAssets()
      .then(response => {
        let playbookList: ICSelectPlaybook[] = [];
        for (let i = 0; i < response.playbooks.length; i++) {
          let currentPlaybook: ICSelectPlaybook = {
            playbookId: response.playbooks[i].id,
            playbookName: response.playbooks[i].name,
            dealspaceId: response.playbooks[i].dealspaceId,
            dealspaceName: response.playbooks[i].dealspaceName,
          };
          playbookList.push(currentPlaybook);
        }
        return playbookList;
      })
      .catch(response => {
        return [];
      });
  }

  async getDealspaceList() {
    return this.coreServices
      .getUserAssets()
      .then(response => {
        let dealspaceList: ICDealspace[] = [];
        for (let i = 0; i < response.dealspaces.length; i++) {
          let currentDealspace: ICDealspace = {
            id: response.dealspaces[i].id,
            name: response.dealspaces[i].name,
            signDate: response.dealspace[i].signDate,
            closeDate: response.dealspace[i].closeDate,
            dayOneDate: response.dealspace[i].dayOneDate,
            buyerCompany: response.dealspace[i].buyerCompany,
            sellerCompany: response.dealspace[i].sellerCompany,
          };
          dealspaceList.push(currentDealspace);
        }
        return dealspaceList;
      })
      .catch(response => {
        return [];
      });
  }

  async storeToken(userToken: string) {
    this.dataManagement.storeData('userToken', userToken);
  }

  async storeRefreshToken(refreshToken: string) {
    this.dataManagement.storeData('refreshToken', refreshToken);
  }

  async logout() {
    this.dataManagement.removeData('userToken');
  }

  async getCurrentUser() {
    return this.coreServices.getUser().then(response => response);
  }

  async updateCurrentUser(currentUser: any) {
    return this.coreServices.updateUser(currentUser).then(response => response);
  } //updatePlaybookSettings

  async updatePlaybookSettings(newValues: PlayBookRequest) {
    return this.coreServices
      .updatePlaybookSettings(newValues)
      .then(response => response);
  }
}

export { ComponentHelper, UserHelper };
